/**
 * Property Profile (Phase 3 redesign).
 *
 * Renders when the user has a single property selected. Reads exclusively
 * from `GET /api/properties/{id}/profile` — no writes, no MMT API touch,
 * no duplicate property record. Encrypted bearer tokens are scrubbed server-
 * side; nothing sensitive reaches this component.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PropertyProfile({ propertyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async (id) => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/properties/${id}/profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setData(r?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(propertyId); }, [propertyId, fetchProfile]);

  if (!propertyId) {
    return (
      <div className="p-6 text-sm text-gray-500" data-testid="property-profile-empty">
        Select a single property from the top-right selector to view its profile.
      </div>
    );
  }
  if (loading && !data) {
    return <div className="p-6 text-sm text-gray-500" data-testid="property-profile-loading">Loading profile…</div>;
  }
  if (error) {
    return (
      <div className="p-4 border-l-4 border-red-500 bg-red-50 text-sm text-red-700" data-testid="property-profile-error">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const p = data.property || {};
  const health = data.integration_health || [];

  return (
    <div className="space-y-6" data-testid="property-profile">
      {/* Header */}
      <div className="p-4 border rounded-lg bg-white flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-800" data-testid="property-profile-name">{p.name}</h2>
            {p.grb_id && (
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-orange-100 text-orange-700"
                    data-testid="property-profile-grb-id">{p.grb_id}</span>
            )}
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
              (p.status || 'active') === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
            }`}>{p.status || 'active'}</span>
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {[p.address, p.city, p.state, p.country].filter(Boolean).join(', ')}
          </div>
        </div>
        <div className="text-right text-xs text-gray-500">
          <div>Property Type: <strong>{p.property_type || '—'}</strong></div>
          <div>Total Rooms: <strong>{p.total_rooms || 0}</strong></div>
          <div>Salesperson: <strong>{p.salesperson_name || '—'}</strong></div>
        </div>
      </div>

      {/* Two-column overview grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Overview" testid="profile-section-overview">
          <KV label="GRB ID"       value={p.grb_id || '—'} mono />
          <KV label="UUID"         value={p.id} mono small />
          <KV label="Property"     value={p.name} />
          <KV label="Type"         value={p.property_type || '—'} />
          <KV label="Status"       value={p.status || 'active'} />
          <KV label="Sales Person" value={p.salesperson_name || '—'} />
          <KV label="OTA Live"     value={p.ota_live_date || '—'} />
        </Section>

        <Section title="Address & Contact" testid="profile-section-contact">
          <KV label="Address"    value={p.address} />
          <KV label="City"       value={p.city} />
          <KV label="State"      value={p.state} />
          <KV label="Country"    value={p.country} />
          <KV label="Pincode"    value={p.pincode || '—'} />
          <KV label="Phone"      value={p.phone} />
          <KV label="Alt Phone"  value={p.alternate_number || '—'} />
          <KV label="Email"      value={p.email} />
          <KV label="Website"    value={p.website || '—'} />
        </Section>

        <Section title="Owner" testid="profile-section-owner">
          <KV label="Name"  value={p.owner_name  || '—'} />
          <KV label="Phone" value={p.owner_phone || '—'} />
          <KV label="Email" value={p.owner_email || '—'} />
        </Section>

        <Section title="Banking & Commission" testid="profile-section-banking">
          <KV label="Bank Name"     value={p.bank_name || '—'} />
          <KV label="Account Name"  value={p.account_holder_name || '—'} />
          <KV label="Account #"     value={p.account_number ? `••••${String(p.account_number).slice(-4)}` : '—'} />
          <KV label="IFSC"          value={p.ifsc_code || '—'} mono />
          <KV label="PAN"           value={p.pan_number || '—'} mono />
          <KV label="GST"           value={p.gst_number || '—'} mono />
          <KV label="Frequency"     value={p.payment_frequency || '—'} />
          <KV label="Commission %"  value={p.commission_agreed != null ? `${p.commission_agreed}%` : '—'} />
        </Section>
      </div>

      {/* Rooms */}
      <Section title={`Rooms · ${data.room_types.length}`} testid="profile-section-rooms">
        {data.room_types.length === 0 ? (
          <div className="text-sm text-gray-500">No rooms configured.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-right">Max Occ</th>
                  <th className="px-3 py-2 text-right">Rooms</th>
                  <th className="px-3 py-2 text-right">Base Rate</th>
                </tr>
              </thead>
              <tbody>
                {data.room_types.map((rt) => (
                  <tr key={rt.id} className="border-t" data-testid={`profile-room-${rt.id}`}>
                    <td className="px-3 py-2 font-medium">{rt.name}</td>
                    <td className="px-3 py-2 text-right">{rt.max_occupancy || 1}</td>
                    <td className="px-3 py-2 text-right">{rt.room_count || 0}</td>
                    <td className="px-3 py-2 text-right">₹{Number(rt.base_rate || 0).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* OTA + Integration health */}
      <Section title="OTAs & Integration Health" testid="profile-section-otas">
        {health.length === 0 ? (
          <div className="text-sm text-gray-500">No OTA mappings configured.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">OTA</th>
                  <th className="px-3 py-2 text-left">Hotel Code</th>
                  <th className="px-3 py-2 text-left">Last Status</th>
                  <th className="px-3 py-2 text-left">Last Sync</th>
                </tr>
              </thead>
              <tbody>
                {health.map((h) => (
                  <tr key={h.ota} className="border-t" data-testid={`profile-ota-${h.ota}`}>
                    <td className="px-3 py-2 font-semibold uppercase">{h.ota}</td>
                    <td className="px-3 py-2 font-mono text-xs">{h.ota_property_code || '—'}</td>
                    <td className="px-3 py-2">
                      {h.last_status ? (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          h.last_status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                          h.last_status === 'error' || h.last_status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{h.last_status}</span>
                      ) : <span className="text-gray-400 text-xs">no sync yet</span>}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-gray-500">
                      {h.last_at ? new Date(h.last_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Recent activity */}
      <Section title="Recent Sync Activity" testid="profile-section-activity">
        {(data.recent_sync_logs || []).length === 0 ? (
          <div className="text-sm text-gray-500">No recent sync activity.</div>
        ) : (
          <ul className="divide-y">
            {data.recent_sync_logs.map((log, i) => (
              <li key={log.id || i} className="py-2 text-sm flex justify-between gap-3" data-testid={`profile-log-${i}`}>
                <div>
                  <div className="text-gray-800">{log.endpoint || log.action || 'sync'}</div>
                  {log.error && <div className="text-xs text-red-600">{log.error}</div>}
                </div>
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  <span className={`inline-block mr-2 text-[10px] px-1.5 py-0.5 rounded ${
                    log.status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-red-100 text-red-700'
                  }`}>{log.status}</span>
                  {log.created_at ? new Date(log.created_at).toLocaleString() : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="border rounded-lg bg-white" data-testid={testid}>
      <div className="px-4 py-2 border-b text-sm font-semibold text-gray-800">{title}</div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function KV({ label, value, mono = false, small = false }) {
  return (
    <div className="flex justify-between items-baseline gap-3 py-1">
      <span className="text-xs text-gray-500">{label}</span>
      <span className={`${mono ? 'font-mono' : ''} ${small ? 'text-[11px]' : 'text-sm'} text-gray-800 text-right break-all`}>
        {value ?? '—'}
      </span>
    </div>
  );
}
